import { Box, Button } from '@evaneos/b2b-ui';
import { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';

import { Done } from '../../../../design-system/Icons/Done';
import { setContractEditStep } from '../../../../resources/contract/actions';
import {
    getContractDisplayPrice,
    getContractViewModel,
} from '../../../../resources/contract/selectors';
import { ContractEditStep, ContractId } from '../../../../resources/contract/types';
import { copyToClipboard } from '../../../../resources/testimonial';
import { getEvaneosHostByLocale } from '../../../../shared/intl/operations';
import { AvailableLocale } from '../../../../shared/intl/types';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import { SidePanelViewMode } from '../../SidePanel/types';
import s from '../Contract.css';

import { ContractInstalmentTable } from './components/ContractInstalmentTable';
import { NoContract } from './components/NoContract';

type Props = {
    contractId: ContractId;
    onEdit?: () => void;
    viewMode?: SidePanelViewMode;
};

export const ContractView = ({ contractId, onEdit, viewMode }: Props): JSX.Element => {
    const dispatch = useAppDispatch();

    const { marketCode: dossierMarketCode } = useGetDossierData(contractId)?.dossier?.project || {};

    const contract = useAppSelector(getContractViewModel);
    const contractPrice = useAppSelector(getContractDisplayPrice);

    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        dispatch(setContractEditStep(ContractEditStep.TRAVEL_INFORMATION));
    }, [dispatch]);

    if (contract === null) {
        return <NoContract dossierId={contractId} onCreate={onEdit} viewMode={viewMode} />;
    }

    const marketCode = dossierMarketCode?.toLowerCase() as AvailableLocale;
    const paymentLink = `https://${getEvaneosHostByLocale(
        marketCode,
    )}/payment-schedule/${contractId}/${contract.paymentSchedule?.token}/`;

    const copyPaymentLink = () => {
        copyToClipboard(paymentLink);
        setLinkCopied(true);
        setTimeout(() => {
            setLinkCopied(false);
        }, 5000);
    };

    return (
        <div
            className={[s.contractContainer, 'body'].join(' ')}
            {...(viewMode && { 'data-contract-view': viewMode })}
        >
            <div className={s.contractCard}>
                <div className={s.headerContainer}>
                    <h1>
                        <FormattedMessage id="contract.titles.contract" />
                    </h1>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {
                                xs: 'column',
                                md: viewMode === 'compact' ? 'column' : 'row',
                            },
                            gap: viewMode === 'expanded' ? 1 : 0,
                        }}
                    >
                        {/* Workaround for DossierRevamp using the Contract components in a SidePanel */}
                        {onEdit ? (
                            <Button
                                variant="outlined"
                                sx={{
                                    mb: viewMode === 'compact' ? 1 : 2,
                                    width: viewMode === 'compact' ? '100%' : 'auto',
                                }}
                                onClick={onEdit}
                            >
                                <FormattedMessage id="contract.edit" />
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                component={Link}
                                sx={{
                                    marginBottom: 2,
                                    marginRight: 1,
                                    marginLeft: { lg: 'auto' },
                                    width: { xs: '100%', lg: 'auto' },
                                }}
                                replace
                                to={{
                                    pathname: `/dossier/${contractId}/contract/edit`,
                                }}
                            >
                                <FormattedMessage id="contract.edit" />
                            </Button>
                        )}
                        {contract.paymentSchedule && (
                            <Button
                                sx={{
                                    width: {
                                        xs: '100%',
                                        lg: viewMode === 'compact' ? '100%' : 'auto',
                                    },
                                    mb: 2,
                                }}
                                onClick={copyPaymentLink}
                            >
                                {linkCopied ? (
                                    <>
                                        <Done color="var(--white)" />
                                        <FormattedMessage id="contract.payment.copied_link" />
                                    </>
                                ) : (
                                    <FormattedMessage id="contract.payment.link" />
                                )}
                            </Button>
                        )}
                    </Box>
                </div>
                <div className={s.summaryLine}>
                    <label>
                        <FormattedMessage id="contract.labels.quote" />
                    </label>
                    {contract.proposal ? (
                        <a href={contract.proposal.url} target="_blank" rel="noopener noreferrer">
                            <FormattedMessage id="contract.labels.download_quote" />
                        </a>
                    ) : (
                        <FormattedMessage id="contract.messages.no_quote" />
                    )}
                </div>
                <div className={[s.summaryLine, s.priceLine].join(' ')}>
                    <label>
                        <FormattedMessage id="contract.labels.amount" />
                    </label>
                    {contractPrice && (
                        <span>
                            <FormattedNumber
                                value={contractPrice.amount as number}
                                style="currency"
                                currency={contractPrice.currency}
                            />
                        </span>
                    )}
                </div>
                <h2>
                    <FormattedMessage id="contract.titles.deposit" />
                </h2>
                <div className={s.instalmentsDiv}>
                    <ContractInstalmentTable
                        instalments={contract.paymentSchedule.depositInstalments}
                        type="deposit"
                    />
                </div>
                <h2>
                    <FormattedMessage id="contract.titles.balance" />
                </h2>
                <div className={s.instalmentsDiv}>
                    <ContractInstalmentTable
                        instalments={contract.paymentSchedule.balanceInstalments}
                        type="balance"
                    />
                </div>
            </div>
        </div>
    );
};
